import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    OnDestroy,
    OnInit,
    ViewEncapsulation
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { IpexFileService } from '../../services/ipex-file.service';
import { MimeMapperService } from '../../services/mime-mapper.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FilePreviewService } from '../../services/file-preview.service';

export interface FilePreviewModalData {
    fileName: string;
    fileId: string;
    previewUrl: string | null; // already-available full-size object URL (optional)
    isPdf: boolean;
    shouldCache: boolean;
}

@Component({
    selector: 'app-file-preview-modal',
    templateUrl: './file-preview-modal.component.html',
    styleUrls: ['./file-preview-modal.component.scss'],
    standalone: false,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilePreviewModalComponent implements OnInit, OnDestroy {
    safeUrl: SafeResourceUrl | null = null;
    private objectUrlToRevoke: string | null = null;
    private destroy$: Subject<void> = new Subject<void>();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: FilePreviewModalData,
        public dialogRef: MatDialogRef<FilePreviewModalComponent>,
        private sanitizer: DomSanitizer,
        private cd: ChangeDetectorRef,
        private readonly ipexFileService: IpexFileService,
        private readonly mimeMapperService: MimeMapperService,
        private readonly filePreviewService: FilePreviewService
    ) {
        if (data.previewUrl) {
            this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(data.previewUrl);
        }
    }

    ngOnInit(): void {
        if (!this.safeUrl && this.data.fileId) {
            this.loadFile();
        }
    }

    private loadFile(): void {
        this.ipexFileService
            .downloadFileById(this.data.fileId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (blob: Blob) => {
                    const mimeType = this.mimeMapperService.getMimeTypeByFileName(this.data.fileName);
                    const fixedBlob = new Blob([blob], { type: mimeType });
                    const objectUrl = URL.createObjectURL(fixedBlob);

                    // caching file if required
                    if (this.data?.shouldCache) {
                        this.filePreviewService.addToCache(this.data.fileId, objectUrl);
                    }

                    this.objectUrlToRevoke = objectUrl;
                    this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(objectUrl);
                    this.cd.detectChanges();
                },
                error: err => {
                    console.error('Failed to load file for preview', err);
                    this.dialogRef.close();
                }
            });
    }

    close(): void {
        this.dialogRef.close();
    }

    ngOnDestroy(): void {
        if (this.objectUrlToRevoke && !this.data?.shouldCache) {
            URL.revokeObjectURL(this.objectUrlToRevoke);
        }

        this.destroy$.next();
        this.destroy$.complete();
    }
}
